import {
  ActiveTradeNextOrder,
  BotActiveTrade,
  Direction,
  GridCardData,
  OrderCardData,
  OrderStatus,
  TradeData,
  TradeExtraInfoOrdersData,
  TradeLadderLevel,
  TradeSourceType,
} from "@shared/types";
import { isTakeProfitOrderType } from "@shared/features/trades/common/utils";
import { countDecimals } from "@shared/utils/numbers";

export type LadderKind = TradeLadderLevel["kind"];
export type LadderTone = LadderKind | "neutral";

export interface LadderLevel extends TradeLadderLevel {
  /** 1-based place in its kind's execution order (E1, TP2…). */
  number?: number;
  /** The order's status as the backend reports it (tooltip only). */
  status?: string;
}

/** More entries than this cluster into bands (DCA bots). */
export const LADDER_CLUSTER_THRESHOLD = 12;

export interface LevelsLadderModel {
  type: "levels";
  isShort: boolean;
  levels: LadderLevel[];
  /**
   * `true` when `levels` lists every order (trade drawer, or the list rows'
   * optional `levels`); `false` for list rows, which only know the next
   * orders (and `progress`).
   */
  isComplete: boolean;
  currentPrice?: number;
  /** Average price of the filled entries. */
  averageEntry?: number;
  /** Filled shares (0–1) of the entries and take-profits. */
  progress?: { entry: number; takeProfit: number };
  /** Closed trades: the price it closed at (drawn instead of the current one). */
  closePrice?: number;
  /** The trade is closed, even when its close price is unknown. */
  isClosed?: boolean;
  /** Closed trades: the backend's final status ("Stopped Out"…). */
  closeStatus?: string;
}

export interface GridLadderModel {
  type: "grid";
  min: number;
  max: number;
  currentPrice?: number;
  /** Open grid orders; `kind` is the pending order (buy = entry, sell = TP). */
  lines?: { price: number; kind?: LadderKind }[];
  /** Completed buy → sell cycles, when known. */
  cycles?: number;
}

export interface SplitLadderModel {
  type: "split";
  /** The side this trade (one leg of a hedging bot) holds. */
  side: Direction;
  /** Long share of the bot's exposure (0–1), when both legs are known. */
  longShare?: number;
}

export type PriceLadderModel =
  | LevelsLadderModel
  | GridLadderModel
  | SplitLadderModel;

/** The span between the filled orders of one kind (see getLadderZones). */
export interface LadderZone {
  tone: "entry" | "take_profit";
  from: number;
  to: number;
}

export interface LadderTick {
  key: string;
  price: number;
  tone: LadderTone;
  filled: boolean;
  /** order: a real order; average: the average entry; bound: a range / cluster end; grid: an open grid order. */
  role: "order" | "average" | "bound" | "grid";
  /** Short label for the large ladder ("E1", "TP2", "SL", "Avg"). */
  label?: string;
  /** Which tick stays when two would overlap. */
  priority: number;
}

/** A label for a band (a DCA's pending entries: "E19–E50"). */
export interface LadderBandLabel {
  key: string;
  from: number;
  to: number;
  title: string;
}

export interface LadderPlan {
  lo: number;
  hi: number;
  ticks: LadderTick[];
  bandLabels: LadderBandLabel[];
  marker?: { price: number; isClose?: boolean; isOutOfRange?: boolean };
}

const clamp01 = (value: number) => Math.min(Math.max(value || 0, 0), 1);

/**
 * A filled share (0–1) as a whole percent, rounded down so it reads "100%"
 * only once fully filled (0.996 → "99%"), and "<1%" for a sliver.
 */
export const formatFilledPercent = (filled: number) => {
  if (filled >= 1 - 1e-9) return "100%";
  if (!(filled > 0)) return "0%";
  return filled < 0.01 ? "<1%" : `${Math.floor(filled * 100)}%`;
};
const isNumber = (value: unknown): value is number =>
  typeof value === "number" && isFinite(value) && value > 0;

/** Entries in execution order (E1 first): by number, else by price. */
export const sortEntries = (entries: LadderLevel[], isShort: boolean) =>
  [...entries].sort((a, b) =>
    a.number !== undefined && b.number !== undefined
      ? a.number - b.number
      : isShort
      ? a.price - b.price
      : b.price - a.price
  );
export const sortTakeProfits = (takeProfits: LadderLevel[], isShort: boolean) =>
  [...takeProfits].sort((a, b) =>
    a.number !== undefined && b.number !== undefined
      ? a.number - b.number
      : isShort
      ? b.price - a.price
      : a.price - b.price
  );

const getZoneLevels = (
  model: LevelsLadderModel,
  kind: LadderZone["tone"]
): LadderLevel[] =>
  (kind === "entry" ? sortEntries : sortTakeProfits)(
    model.levels.filter(
      (level) => level.kind === kind && isNumber(level.price)
    ),
    model.isShort
  );

/**
 * A zone covers only orders that actually filled: from the first filled order
 * to the furthest one (entries 100 / 95 / 90 with the price at 94 → 95–100).
 * Without the lowest or highest price reached, the space between a filled
 * order and the current price stays neutral; a single filled order shows as
 * its solid tick alone.
 */
const getFilledZone = (
  tone: LadderZone["tone"],
  levels: LadderLevel[]
): LadderZone[] => {
  const prices = levels
    .filter((level) => level.filled > 0)
    .map((level) => level.price);
  if (prices.length < 2) return [];
  return [{ tone, from: Math.min(...prices), to: Math.max(...prices) }];
};

export const getLadderZones = (model: LevelsLadderModel): LadderZone[] => [
  ...getFilledZone("entry", getZoneLevels(model, "entry")),
  ...getFilledZone("take_profit", getZoneLevels(model, "take_profit")),
];

export const isClusteredLadder = (model: LevelsLadderModel) =>
  model.isComplete &&
  model.levels.filter((level) => level.kind === "entry").length >
    LADDER_CLUSTER_THRESHOLD;

const withDomain = (
  prices: number[],
  plan: Omit<LadderPlan, "lo" | "hi">
): LadderPlan => {
  const valid = prices.filter(isNumber);
  let lo = Math.min(...valid);
  let hi = Math.max(...valid);
  if (!valid.length) {
    lo = 0;
    hi = 1;
  } else if (hi - lo < hi * 1e-6) {
    // A single price: centre it in a ±1% window.
    lo = lo * 0.99;
    hi = hi * 1.01;
  }
  return { lo, hi, ...plan };
};

const PRIORITY = {
  stop: 6,
  average: 5,
  filled: 4,
  bound: 3,
  pending: 2,
  grid: 1,
};

const getLevelsPlan = (model: LevelsLadderModel): LadderPlan => {
  const { isShort, isComplete, averageEntry, currentPrice, closePrice } = model;
  const entries = sortEntries(
    model.levels.filter((level) => level.kind === "entry"),
    isShort
  );
  const takeProfits = sortTakeProfits(
    model.levels.filter((level) => level.kind === "take_profit"),
    isShort
  );
  const stops = model.levels.filter((level) => level.kind === "stop");
  const clustered = isClusteredLadder(model);
  const average = isNumber(averageEntry) ? averageEntry : undefined;
  const isClosed = isNumber(closePrice);
  const markerPrice = isClosed
    ? closePrice
    : isNumber(currentPrice)
    ? currentPrice
    : undefined;
  const ticks: LadderTick[] = [];
  const bandLabels: LadderBandLabel[] = [];
  const entryLabel = (entry: LadderLevel, index: number) =>
    `E${entry.number ?? index + 1}`;

  const orderTick = (
    level: LadderLevel,
    key: string,
    label?: string
  ): LadderTick => ({
    key,
    price: level.price,
    tone: level.kind,
    filled: level.filled > 0,
    role: "order",
    label,
    priority:
      level.kind === "stop"
        ? PRIORITY.stop
        : level.filled > 0
        ? PRIORITY.filled
        : PRIORITY.pending,
  });

  // Entries: one tick each, or (DCA) the filled and the pending ones as bands.
  if (clustered) {
    let lastFilled = -1;
    entries.forEach((entry, index) => {
      if (entry.filled > 0) lastFilled = index;
    });
    if (lastFilled >= 0) {
      const first = entries[0];
      const last = entries[lastFilled];
      ticks.push({
        ...orderTick(first, "entry-first", entryLabel(first, 0)),
        role: "bound",
      });
      if (lastFilled > 0) {
        ticks.push({
          ...orderTick(last, "entry-last-filled", entryLabel(last, lastFilled)),
          role: "bound",
        });
      }
    }
    const pending = entries.slice(lastFilled + 1);
    if (pending.length > 1) {
      const first = pending[0];
      const last = pending[pending.length - 1];
      bandLabels.push({
        key: "cluster-pending",
        from: first.price,
        to: last.price,
        title: `${entryLabel(first, lastFilled + 1)}–${entryLabel(
          last,
          entries.length - 1
        )}`,
      });
      ticks.push(
        { ...orderTick(first, "entry-next"), role: "bound" },
        { ...orderTick(last, "entry-last"), role: "bound" }
      );
    } else if (pending.length === 1) {
      ticks.push(
        orderTick(
          pending[0],
          "entry-next",
          entryLabel(pending[0], lastFilled + 1)
        )
      );
    }
  } else {
    entries.forEach((entry, index) =>
      ticks.push(
        orderTick(
          entry,
          `entry-${index}`,
          isComplete ? entryLabel(entry, index) : undefined
        )
      )
    );
  }
  if (average !== undefined && (clustered || !isComplete)) {
    ticks.push({
      key: "average",
      price: average,
      tone: "entry",
      filled: true,
      role: "average",
      priority: PRIORITY.average,
    });
  }
  takeProfits.forEach((level, index) =>
    ticks.push(
      orderTick(
        level,
        `take-profit-${index}`,
        isComplete ? `TP${level.number ?? index + 1}` : undefined
      )
    )
  );
  stops.forEach((level, index) =>
    ticks.push(orderTick(level, `stop-${index}`, "SL"))
  );

  return withDomain(
    [
      ...model.levels.map((level) => level.price),
      ...(average !== undefined ? [average] : []),
      ...(markerPrice !== undefined ? [markerPrice] : []),
    ],
    {
      ticks,
      bandLabels,
      marker:
        markerPrice !== undefined
          ? { price: markerPrice, isClose: isClosed }
          : undefined,
    }
  );
};

export const isGridOutOfRange = (model: GridLadderModel) =>
  isNumber(model.currentPrice) &&
  (model.currentPrice < model.min || model.currentPrice > model.max);

const getGridPlan = (model: GridLadderModel, nearLines: number): LadderPlan => {
  const { min, max, currentPrice } = model;
  const ticks: LadderTick[] = [
    {
      key: "grid-min",
      price: min,
      tone: "neutral",
      filled: true,
      role: "bound",
      priority: PRIORITY.bound,
    },
    {
      key: "grid-max",
      price: max,
      tone: "neutral",
      filled: true,
      role: "bound",
      priority: PRIORITY.bound,
    },
  ];
  // Only the open orders closest to the price, on each side.
  if (isNumber(currentPrice) && model.lines?.length) {
    const open = model.lines.filter(
      (line) => line.kind && line.price > min && line.price < max
    );
    const below = open
      .filter((line) => line.price < currentPrice)
      .sort((a, b) => b.price - a.price)
      .slice(0, nearLines);
    const above = open
      .filter((line) => line.price >= currentPrice)
      .sort((a, b) => a.price - b.price)
      .slice(0, nearLines);
    [...below, ...above].forEach((line, index) =>
      ticks.push({
        key: `grid-${index}`,
        price: line.price,
        tone: line.kind!,
        filled: false,
        role: "grid",
        priority: PRIORITY.grid,
      })
    );
  }
  return withDomain(
    [min, max, ...(isNumber(currentPrice) ? [currentPrice] : [])],
    {
      ticks,
      bandLabels: [],
      marker: isNumber(currentPrice)
        ? { price: currentPrice, isOutOfRange: isGridOutOfRange(model) }
        : undefined,
    }
  );
};

export const getLadderPlan = (
  model: LevelsLadderModel | GridLadderModel,
  options: { nearGridLines?: number } = {}
): LadderPlan =>
  model.type === "grid"
    ? getGridPlan(model, options.nearGridLines ?? 2)
    : getLevelsPlan(model);

/** Horizontal position (0–1) of a price, keeping `padding` free at both ends. */
export const getLadderX = (plan: LadderPlan, price: number, padding = 0) =>
  padding +
  (1 - 2 * padding) *
    Math.min(Math.max((price - plan.lo) / (plan.hi - plan.lo), 0), 1);

/**
 * Drops ticks that would overlap (closer than `minGap` px) at this width,
 * keeping the more important one (stop, average entry, filled orders…); the
 * tooltip still lists every level.
 */
export const resolveTickOverlaps = (
  plan: LadderPlan,
  width: number,
  padding: number,
  minGap: number
) => {
  if (!width) return plan.ticks;
  const placed = plan.ticks
    .map((tick) => ({ tick, x: getLadderX(plan, tick.price, padding) * width }))
    .sort((a, b) => a.x - b.x);
  const kept: typeof placed = [];
  placed.forEach((item) => {
    const previous = kept[kept.length - 1];
    if (previous && item.x - previous.x < minGap) {
      if (item.tick.priority > previous.tick.priority) {
        kept[kept.length - 1] = item;
      }
      return;
    }
    kept.push(item);
  });
  return kept.map((item) => item.tick);
};

/* ------------------------------------------------------------------ */
/* Models from the app's data                                          */
/* ------------------------------------------------------------------ */

export const numberLevels = (levels: TradeLadderLevel[]): LadderLevel[] => {
  const counters: Record<LadderKind, number> = {
    entry: 0,
    take_profit: 0,
    stop: 0,
  };
  return levels
    .filter((level) => isNumber(level.price))
    .map((level) => ({
      ...level,
      filled: clamp01(level.filled),
      number: ++counters[level.kind],
    }));
};

/**
 * A trades-list row's ladder. Rows don't carry every order, so unless the
 * backend sends `levels` it is the simplified one: the stop, the next entry,
 * the average entry, the next take-profit and the price. Grid trades draw
 * their range; hedging bot legs their long/short split.
 */
export const getTradeRowLadder = (
  trade: TradeData,
  isOpen: boolean,
  hedgeLongShare?: number
): PriceLadderModel => {
  const graphData = trade.graph_data;
  const isShort = trade["Long/Short"] === Direction.SHORT;
  if (graphData && "current_price" in graphData) {
    return {
      type: "grid",
      min: graphData.min_price,
      max: graphData.max_price,
      currentPrice: isOpen ? graphData.current_price : undefined,
      lines: trade.levels
        ?.filter((level) => level.kind !== "stop" && isNumber(level.price))
        .map((level) => ({ price: level.price, kind: level.kind })),
      cycles: trade.grid_cycles,
    };
  }
  if (trade.source_type === TradeSourceType.HEDGING_BOT) {
    return {
      type: "split",
      side: trade["Long/Short"],
      longShare: hedgeLongShare,
    };
  }
  const entryProgress = clamp01(graphData?.entry_progress ?? 0);
  const takeProfitProgress = clamp01(
    (graphData && "take_profit_progress" in graphData
      ? graphData.take_profit_progress
      : 0) ?? 0
  );
  const averageEntry =
    entryProgress > 0 && isNumber(trade.entry_price)
      ? trade.entry_price
      : undefined;
  // No fallback to `trade.price` (it can be the live price): without a known
  // close price the closed row shows none rather than a misleading one.
  const closePrice =
    !isOpen && isNumber(trade.price_when_closed)
      ? trade.price_when_closed
      : undefined;
  const base = {
    type: "levels" as const,
    isShort,
    currentPrice: isOpen && isNumber(trade.price) ? trade.price : undefined,
    averageEntry,
    closePrice,
    isClosed: !isOpen,
    closeStatus: isOpen ? undefined : trade.trade_status || undefined,
    progress: { entry: entryProgress, takeProfit: takeProfitProgress },
  };
  if (trade.levels?.length) {
    return { ...base, isComplete: true, levels: numberLevels(trade.levels) };
  }
  const levels: LadderLevel[] = [];
  if (isOpen) {
    const add = (kind: LadderKind, rawPrice?: number | string): void => {
      const price = Number(rawPrice);
      if (isNumber(price)) levels.push({ kind, price, filled: 0 });
    };
    add("stop", trade.next_stop?.price);
    add("entry", trade.next_entry?.price);
    add("take_profit", trade.next_take_profit?.price);
  }
  return { ...base, isComplete: false, levels };
};

const orderFilled = (order: OrderCardData) => {
  if (
    order.status?.toLowerCase().startsWith(OrderStatus.FULFILLED.toLowerCase())
  )
    return 1;
  if (order.quantity > 0)
    return clamp01(order.filled_quantity / order.quantity);
  return clamp01(order.ratio_filled);
};

const roundToOrderDecimals = (value: number, prices: number[]) => {
  const decimals = Math.max(
    ...prices.map((price) => countDecimals(price) ?? 0)
  );
  return Number(value.toFixed(Math.min(decimals, 12)));
};

/** The trade drawer's full ladder, from the orders it loads. */
export const getOrdersLadder = (
  orders: TradeExtraInfoOrdersData,
  options: {
    currentPrice?: number;
    averageEntry?: number;
    isOpen: boolean;
    closeStatus?: string;
  }
): LevelsLadderModel => {
  const levels: LadderLevel[] = [];
  const add = (kind: LadderKind, cards: OrderCardData[] | undefined) =>
    cards?.forEach((order, index) => {
      const filled = orderFilled(order);
      const isCancelled = order.status === OrderStatus.CANCELLED;
      if (
        (options.isOpen && isCancelled && filled === 0) ||
        !isNumber(order.target_price)
      )
        return;
      levels.push({
        kind,
        price: order.target_price,
        filled,
        number: order.number ?? index + 1,
        status: order.status,
      });
    });
  add("entry", orders.entry_order_cards);
  add("take_profit", orders.take_profit_order_cards);
  add("stop", orders.stop_order_cards);

  const hasFilledEntries = levels.some(
    (level) => level.kind === "entry" && level.filled > 0
  );
  // The average of the filled entries, weighted by their filled quantity.
  let averageEntry = options.averageEntry;
  if (!isNumber(averageEntry)) {
    const filled = (orders.entry_order_cards || []).filter(
      (order) => order.filled_quantity > 0
    );
    const quantity = filled.reduce((sum, o) => sum + o.filled_quantity, 0);
    averageEntry = quantity
      ? filled.reduce((sum, o) => sum + o.target_price * o.filled_quantity, 0) /
        quantity
      : undefined;
  }
  // Closed trades: where the position was sold, weighted by quantity. An
  // estimate from the exit orders' target prices: the backend doesn't send
  // the actual fill prices.
  let closePrice: number | undefined;
  if (!options.isOpen) {
    const exits = [
      ...(orders.take_profit_order_cards || []),
      ...(orders.stop_order_cards || []),
    ].filter((order) => order.filled_quantity > 0);
    const quantity = exits.reduce((sum, o) => sum + o.filled_quantity, 0);
    closePrice = quantity
      ? roundToOrderDecimals(
          exits.reduce(
            (sum, o) => sum + o.target_price * o.filled_quantity,
            0
          ) / quantity,
          exits.map((order) => order.target_price)
        )
      : undefined;
  }
  return {
    type: "levels",
    isShort: !!orders.reverse,
    levels,
    isComplete: true,
    currentPrice: options.isOpen ? options.currentPrice : undefined,
    averageEntry: hasFilledEntries ? averageEntry : undefined,
    closePrice,
    isClosed: !options.isOpen,
    closeStatus: options.isOpen ? undefined : options.closeStatus,
  };
};

/** A grid trade's ladder, with its open orders when the grids are loaded. */
export const getGridLadder = (
  graphData: { min_price: number; max_price: number },
  currentPrice: number | undefined,
  grids?: GridCardData[]
): GridLadderModel => ({
  type: "grid",
  min: graphData.min_price,
  max: graphData.max_price,
  currentPrice,
  lines: grids
    ?.filter((grid) => isNumber(grid.price))
    .map((grid) => ({
      price: grid.price,
      kind: grid.pending_order_dict
        ? isTakeProfitOrderType(grid.pending_order_dict.type)
          ? "take_profit"
          : "entry"
        : undefined,
    })),
  // Each filled take-profit closes one buy → sell cycle.
  cycles: grids?.reduce(
    (sum, grid) => sum + (grid.num_of_filled_take_profits || 0),
    0
  ),
});

/* ------------------------------------------------------------------ */
/* Hedging bots: the long/short split from both legs                   */
/* ------------------------------------------------------------------ */

const hedgeSplitsCache = new WeakMap<TradeData[], Map<string, number>>();
const hedgeKey = (trade: TradeData) =>
  `${trade.bot_id}|${trade.client_id}|${trade.symbol}`;

/**
 * The long share of a hedging bot's exposure for one of its legs, from the
 * quantities both legs hold (same symbol, so the price cancels out).
 * `undefined` when the other leg isn't in the list.
 */
export const getHedgeLongShare = (
  trade: TradeData,
  trades: TradeData[] | undefined
) => {
  if (!trades || trade.source_type !== TradeSourceType.HEDGING_BOT) return;
  let splits = hedgeSplitsCache.get(trades);
  if (!splits) {
    const legs = new Map<string, { long?: number; short?: number }>();
    trades.forEach((item) => {
      if (item.source_type !== TradeSourceType.HEDGING_BOT) return;
      const key = hedgeKey(item);
      const leg = legs.get(key) || {};
      const quantity = Math.abs(item.invested_quantity || 0);
      if (item["Long/Short"] === Direction.SHORT) leg.short = quantity;
      else leg.long = quantity;
      legs.set(key, leg);
    });
    splits = new Map();
    legs.forEach((leg, key) => {
      if (leg.long === undefined || leg.short === undefined) return;
      const total = leg.long + leg.short;
      if (total > 0) splits!.set(key, leg.long / total);
    });
    hedgeSplitsCache.set(trades, splits);
  }
  return splits.get(hedgeKey(trade));
};

/** Filled / total orders per kind, for captions (complete ladders only). */
export const getLadderCounts = (model: LevelsLadderModel) => {
  const count = (kind: LadderKind) => {
    const levels = model.levels.filter((level) => level.kind === kind);
    return {
      filled: levels.filter((level) => level.filled > 0).length,
      total: levels.length,
    };
  };
  return {
    entries: count("entry"),
    takeProfits: count("take_profit"),
    stops: count("stop"),
  };
};

const priceFromDistance = (order?: ActiveTradeNextOrder) => {
  const price = Number(order?.price);
  const distance = Number(order?.distance);
  return isNumber(price) && isFinite(distance) && distance > -1
    ? price / (1 + distance)
    : undefined;
};

const getNextLevels = (trade: BotActiveTrade): LadderLevel[] =>
  (
    [
      ["stop", trade.next_stop],
      ["entry", trade.next_entry],
      ["take_profit", trade.next_take_profit],
    ] as [LadderKind, ActiveTradeNextOrder | undefined][]
  )
    .filter(([, order]) => isNumber(Number(order?.price)))
    .map(([kind, order]) => ({ kind, price: Number(order?.price), filled: 0 }));

const isShortLadder = (levels: LadderLevel[], currentPrice?: number) => {
  const pending = (kind: LadderKind) =>
    levels.find((level) => level.kind === kind && !level.filled);
  const takeProfit = pending("take_profit");
  const stop = pending("stop");
  if (!currentPrice) return false;
  if (takeProfit) return takeProfit.price < currentPrice;
  return !!stop && stop.price > currentPrice;
};

export const getBotTradeLadder = (
  trade: BotActiveTrade,
  livePrice?: number,
  botReverse?: boolean | null
): LevelsLadderModel => {
  const currentPrice = isNumber(livePrice)
    ? livePrice
    : [trade.next_take_profit, trade.next_entry, trade.next_stop]
        .map(priceFromDistance)
        .find(isNumber);
  const isComplete = !!trade.levels?.length;
  const levels = isComplete
    ? numberLevels(trade.levels as TradeLadderLevel[])
    : getNextLevels(trade);
  return {
    type: "levels",
    isShort: trade.reverse ?? botReverse ?? isShortLadder(levels, currentPrice),
    levels,
    isComplete,
    currentPrice,
  };
};
